export interface IResource {
    id: number;
}

export interface INormalizedResourceList<T extends IResource> {
    [id: number]: T
}

/* General
============================================*/

export interface ISupportRequest {
    userId: number;
    subject: string;
    body: string;
}

export enum FeedbackType {
    MedexInsights = 0
}
export interface IFeedbackRequest {
    id: number;
    userId: number;
    type: FeedbackType;
    feedback: string;
    projectFileId: number;
    isHelpful?: Boolean;
}

export interface IAppInfo {
    medexVersion: string;
    referenceLibraryFileCount: number;
    referenceLibraryLastUpdated: string;
}

export interface IClientDownload {
    version: string;
    updated: string;
    link: string;
}

/* User
============================================*/

export interface IUser extends IResource {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    userGroups: IUserGroupUser[];
    permissions: IUserGroupPermission[];
    isAdmin: boolean;
    newPassword?: string;
    verified: boolean;
    preferences?: IUserPreferences;
}

export interface IUserPreferences {
    userId: number;
    projectNotifications: boolean;
    fileNotifications: boolean;
    referenceLibraryNotifications: boolean;
}

export interface IUserInviteResponse {
    userId: number;
    token: string;
    password?: string;
}

export interface IAutocompleteResult {
    id: number;
    displayText: string;
}

/* User Groups
============================================*/

export interface IUserGroup extends IResource {
    name: string;
    description: string;
    permissions: IGroupPermissionDetails[];
    users: IUserGroupUser[];
    isDefault: boolean;
}

export interface IUserGroupUser {
    userId: number;
    userGroupId: number;
}

export interface IGroupPermissionDetails {
    description: string;
    permission: Permission;
}

export interface IUserGroupPermission {
    userId: number;
    userGroupId: number;
    permission: Permission;
}

export enum Permission {
    Public = 0,
    ReadGroupUsers = 10,
    EditGroupUsers = 11,
    DeleteGroupUsers = 12,
    EditGroupSettings = 20,
    ReadProjects = 30,
    EditProjects = 31,
    DeleteProjects = 32,
    ReadFiles = 40,
    EditFiles = 41,
    DeleteFiles = 42,
    DownloadReports = 51,
    RequestCaseAnalysis = 60
}

/* Roles
============================================*/

export interface IRole {
    id: number;
    name: string;
    description: string;
}

/* Projects
============================================*/

export interface IProject {
    id: number;
    userId: number;
    userGroupId: number;
    caseId: string;
    examinerName: string;
    labId: string;
    created?: string;
    updated?: string;
    submitted?: string;
    status: ProjectStatus;
    files: IProjectFile[];
    useEva?: boolean;
}

export enum ProjectStatus {
    Draft = 1,
    InProgress = 2,
    Complete = 3,
    Cancelled = 4,
    Error = 99
}

export interface IProjectSummary {
    project: IProject;
    files: IProjectFile[];
    medexVersion: string;
    referenceLibraryVersion: string;
    filesSubmitted: number;
    filesSupported: number;
    filesWithKnownSignature: number;
}

/* Project Files
============================================*/

export interface IProjectFile {
	id: number;
	projectId: number;
	originalFilename: string;
	path?: string;
	filename?: string;
	aquisitionType: FileAquisitionMethod;
	status: FileStatus;
	flagStatus: FileFlagStatus;
	created?: string;
	updated?: string;
	sha256Hash: string;
	signature1RefMatch: boolean;
	signature2RefMatch: boolean;
	originatingDevice?: string;
	lastGeneration?: string;
	modificationStatus?: FileModificationStatus;
	referenceVersion?: string;
    uploadId: string;
    rating?: IProjectFileRating;
    file?: File;
	embeddedModelDiscrepancy: boolean;
    evaStatus: EvaStatus;
}

export interface IProjectFileResultSummary {
	projectFileId: number;
	projectId: number;
	filename: number;
	signatureMatchType?: SignatureMatchType;
	confidence: string;
	brand?: string;
	model?: string;
	lastGeneration?: string;
	modificationStatus?: FileModificationStatus;
	status: FileStatus;
	signature1Description?: string;
	signature2Description?: string;
	lastGenerationCategory?: string;
	evaStatus?: EvaStatus;
}
export interface IProjectFileChunk {
    data: Blob | null;
    fileId: number;
    filename: string;
    originalFilename: string;
    part: number;
    parts: number;
    projectId: number;
    status: FileChunkStatus;
    uploadId: string;
}

export enum SignatureMatchType {
    NoMatch = 0,
    FullMatch = 1,
    PartialMatch = 2
}
export enum MedexInsightType {
    BRAND_INDICATOR = 1,
    DEVICE_INDICATOR = 2,
    LAST_GENERATION_INDICATOR = 3,
    NOT_ORIGINAL_DEVICE_INDICATOR = 4,
    AUTOMATED_INSIGHT = 5,
    APPLE_CREATE_DATE_INDICATOR = 6,
    GENERAL_DATE_INDICATOR = 7,
    EVA_ANALYSIS = 8,
    PDF_INSIGHT = 9
}
export interface IMedexInsight {
    insightType: MedexInsightType;
    description: string;
    evaFinalSummary?: string;
    evaProvenanceCategory?: string;
    evaKeyFindings?: string[];
}

export const ProvenanceCategoryIcons: Record<string, string> = {
    'Camera Original':                                          'mdi-camera',
    'File Editing Application':                                 'mdi-keyboard',
    'Generative AI / Deepfake':                                 'mdi-robot',
    'IP Camera System / VMS / DVR / NVR / CCTV / Drone':       'mdi-cctv',
    'Media Management Application':                             'mdi-apps',
    'Mobile Application':                                       'mdi-cellphone-screenshot',
    'Mobile Device (General)':                                  'mdi-cellphone',
    'Screen Recorder':                                          'mdi-monitor-screenshot',
    'Social Media':                                             'mdi-share-variant',
    'Transmission / Acquisition App':                           'mdi-swap-horizontal',
    'Undetermined':                                             'mdi-help-circle-outline',
    'Website':                                                  'mdi-web',
};

export enum FileAquisitionMethod {
    NotSet = 0,
    DirectUpload,
    DirectUrl,
    YouTubeDL,
    Facebook,
    MetadataUpload
}

export enum FileStatus {
    Uploading = 1,
    UploadComplete,
    Downloading,
    DownloadComplete,
    Processing,
    ProcessingComplete,
    Draft,
    ProcessingCompleteUnsupported,
    UploadError,
    Cancelled = 10,
    Error = 99
}

export enum FileFlagStatus {
    NotFlagged = 1,
    Flagged = 2,
    Matched = 3
}

export enum EvaStatus {
    NotRequested = 0,
    Pending = 1,
    Running = 2,
    Complete = 3,
    Error = 99
}

export interface IFileGenerationHistory {
    generationHistory: IFileGenerationList[];
    lastGenerations: string[];
    lastGenerationCategory: string;
    originalDevice: boolean;
}

export interface IFileGenerationList {
    fileCount: number;
    deviceCount: number;
    generations: IFileGeneration[];
}

export interface IFileGeneration {
    count: number;
    description: string;
    index: number;
    type: FileGenerationType;
    processes?: string[];
}
export interface IFileDeviceGeneration {
    modelName: string;
    brandName: string;
    ecaMatch: boolean;
    signatureMatch: boolean;
    generations?: IFileGeneration[];
}
export interface IFileOriginatingDevice {
    classifiedBrand: string;
    classifiedDevice: string;
    matchType: SignatureMatchType;
    signatureBrands: string[];
    signatureDevices: string[];
}

export enum SignatureMatchType {
    FULL_MATCH,
    PARTIAL
}

export enum FileModificationStatus {
    NotModified = 1,
    PossiblyModified,
    Modified
}

export enum FileGenerationType {
    OriginalDevice = 1,
    Generation
}

export interface IFileExaminationResult {
    mediaType: MediaType;
    file: IProjectFile;
	summary: IFileSummary;
	generationHistory: IFileGenerationHistory,
    originatingDevice: IFileOriginatingDevice,
	metadata: Record<string, string>;
	testResults: IFileTestResults;
	proprietaryStructures: IFileStructure[];
    unknownStructures: IFileStructure[];
    deviceGenerationHistory: IFileDeviceGeneration[];
    matchHistory: IFileDeviceGeneration[];
	fileNotes: string;
    medexInsights: IMedexInsight[];
    c2PA: IProjectFileC2PA;
    lastGenerationClassification: IFileClassification;
    modelClassification: IFileClassification;
    brandClassification: IFileClassification;
    gps: IFileGpsData;
    fileEncoderConfigurationAnalysis: IFileEncoderConfigurationAnalysisResource;
    timeBasedMetadata: ITimeBasedMetadata;
    documentMetadata: IDocumentMetadata | null;
    dataConsistency: IDataConsistencyRow[] | null;
}

export interface IAddUserResource {
    canAdd: boolean;
    quantity: number;    
}
export interface IFileClassification {
    predictedValue: string;
    predictedProbability: number;    
}
export interface IFileExaminationReportOptions {
    includeProjectInformation: boolean;
    includeFileSummary: boolean;
    includeSimilarityAnalysisResult: boolean;
    includeStructureAnalysis: boolean;
    includeModificationTests: boolean;
    includeValidationTests: boolean;
    includeFileComments: boolean;
    includeFileMetadata: boolean;
    includeStructuralData: boolean;
    includeProprietaryData: boolean;
    includeUnknownData: boolean;
    includeMedexInsights: boolean;
    includeC2PA: boolean;
    includeTimeBasedMetadata: boolean;
}

export interface IFileSummary {
	filename: string;
	format: string;
	projectId: string;
	processed: string;
	checksum: string;
	medexVersion: string;
	referenceLibraryVersion: string;
	fileSignature: string;
}

export interface IProjectFileC2PA {
	id: number;
	projectFileId: number;
	hasManifest: boolean;
	hasValidManifest: boolean;
}

export interface IFileTestResults {
	validationResults: IFileTestResult[];
	modificationResults: IFileTestResult[];
}

export interface IFileTestResult {
	passed: boolean;
	description: string;
	errorDescription: string;
	start: number;
	end: number;
}

export interface IFileStructure {
    id: number;
    projectFileId: number;
	name: string;
	size: number;
	start: number;
	end: number;
    hasRemotePath: boolean;
    category?: string;
    role?: string;
    encrypted?: boolean;
    truncated?: boolean;
}

export interface IFileStructureData {
    binary: string;
    hex: string;
    text: string;
    url: string;
}

export interface IFileSignature {
    structures: IFileSignatureStructure[];
}

export interface IFileSignatureStructure {
    id: number;
    name: string;
    depth: number;
    percentage: number;
    children: IFileSignatureStructure[];
    sequenceHashId?: number;
    sequenceHashDescription?: string;
    sequenceHashLabel?: String;
    sequenceHashLabelType: SequenceLabelType;
}
export interface IInsightTest
{
    id: string;
    label: string;
    name: string;
    value: boolean | undefined;
}
export interface IPathEvaluationResult
{
    tests: IInsightTest[];
    match: boolean;
    resultText: string;
    pathId: number;
}
export enum SequenceLabelType {
    Device = 1,
    Brand,
    LastGeneration
}

export enum MediaType {
    Video = 0,
    Image,
    Document
}

export interface IDataConsistencyRow {
    id: number;
    projectFileId: number;
    ruleId: string;
    description: string;
    observationMessage: string;
    start: number;
    end: number;
}

export interface IMetadataRow {
    id: number;
    projectFileId: number;
    section: string;
    displayName: string;
    key: string;
    value: string;
    index: number;
}

export interface IMetadataImage {
    id: number;
    objectNumber: number;
    source: string;
    filters: string;
    declaredFormat: string;
    extractType: string;
    width: number | null;
    height: number | null;
    bitsPerComponent: number | null;
    declaredLength: number | null;
    startOffset: number | null;
    endOffset: number | null;
    length: number | null;
    remotePath: string | null;
}

export interface IMetadataFont {
    id: number;
    objectNumber: number;
    subtype: string;
    baseFont: string;
    baseFontWithoutSubsetTag: string;
    fontName: string | null;
    isEmbedded: boolean;
    fontFileKey: string | null;
    hasSubsetTag: boolean;
    startOffset: number | null;
    endOffset: number | null;
    length: number | null;
}

export interface IMetadataAttachment {
    id: number;
    filespecObject: number;
    nameF: string | null;
    nameUF: string | null;
    description: string | null;
    detectedFormat: string | null;
    decodedLength: number | null;
    sha256: string | null;
    creationDate: string | null;
    modDate: string | null;
    startOffset: number | null;
    endOffset: number | null;
    length: number | null;
    decodeStatus: string | null;
    objectNumber: number | null;
    generation: number;
}

export interface IDocumentMetadata {
    metadata: IMetadataRow[];
    images: IMetadataImage[];
    fonts: IMetadataFont[];
    attachments: IMetadataAttachment[];
}
export interface IProjectFileComment {
    id: number;
    projectFileId: number;
    userId: number;
    body: string;
    created: string;
    edited?: string;
    createdBy?: string;
    editedBy?: string;
}

export interface IProjectFileRating {
    id: number;
    projectFileId: number;
    userId: number;
    rating: number;
    description: string;
    created: string;
    updated?: string;
}

export interface IPublicFileLog {
    id: number;
    projectFileId: number;
    message: string;
    created: string;
    visible: boolean;
}

/* Uploads
============================================*/

export enum FileChunkStatus {
    Queued = 1,
    Uploading = 2,
    Complete = 3,
    Retrying = 4,
    Reading = 5,
    Error = 99
}

export enum FileUploadStatus {
	NotStarted=1,
	Uploading=2,
	Complete=3,
	Retrying=4,
	Offline=5,
	Paused=6,
    Completing=7,
    Canceled=8,
	Error=99,
	FileUploadStatus
}

export type FileUploadProgress = {
    filename: string;
    percent: number;
    status: FileUploadStatus,
    currentChunk: number;
    totalChunks: number;
}

export enum FileUploadEvent {
    Complete = 'complete',
    Progress = 'progress',
    Retry = 'retry',
    Error = 'error',
    Online = 'online',
    Offline = 'offline'
};



/* Subscribers
============================================*/

export interface ISubscriber {
    id: number;
    name: string;
    contactEmail?: string;
    subscriptionType: SubscriptionType;
    logoImageUrl?: string;
    allowClientDownload?: boolean;
    evaEnabled: boolean;
}

export enum SubscriptionType {
    Basic = 1,
    Advanced
}

/* Device Lists
============================================*/

export interface IDeviceList {
    deviceBrands: IDeviceBrand[];
    lastGenerations: ILastGeneration[];
    deviceGenerations: IDeviceBrandGeneration[];
    deviceAttributeIndex: string[];
    lastGenerationAttributeIndex: string[];
}

export interface ILastGeneration {
    name: string;
    attributes: number[];
}
export interface IDeviceBrand {
    brandName: string;
    modelName: string;
    attributes: number[];
}
export interface IDeviceModel {
    modelName: string;
    attributes: number[];
}

export interface IDeviceBrandGeneration {
    brandName: string;
    modelGenerations: IModelGeneration[];
}

export interface IModelGeneration {
    modelName: string;
    generations: string[];
}

/**
 * Dashboard
 */


export interface IDashboardResource {
    referenceDashboard: IReferenceDashboardResource;
    fileCountDashboard: IFileCountDashboardResource;
    referenceMatchDashboard: IPieDatasetResource;
    lastGenerationCategoryDashboard: IPieDatasetResource;
}
export interface IFileCountDashboardResource {
    monthLabels: string[];
    datasets: IDashboardDatasetResource[];
}
export interface IReferenceDashboardResource {
    referenceVersion: string;
    referenceFiles: number;
    monthLabels: string[];
    datasets: IDashboardDatasetResource[];
}
export interface IDashboardDatasetResource {
    data: number[];
    label: string;
}
export interface IPieDatasetResource {
    data: number[];
    labels: string[];
}

// ECA Analysis
export interface IFileEncoderConfigurationAnalysisResource{
    matches: IProjectFileEncoderResource[];
    type: number;
    value: string;
    showECA: boolean;
}

export interface IProjectFileEncoderResource{
    projectFileId: number;
    brandName: string;
    modelName: string;
    lastGeneration: string;
    configurationType: number;
    matchType: number;
}
export interface IFileGpsData {
    projectFileId: number;
    hasGpsData: boolean;
    latitude?: number;
    longitude?: number;
    altitude?: number;
}

export interface ITimeBasedMetadata {
    location: ILocationEntry[];
    slte: ISiteEntry[];
    sensor: ISensorEntry[];
    detectedFaces: IDetectedFaceEntry[];
    livePhotoInfo: ILivePhotoInfoEntry[];
    transcription: IGooglePixelTranscriptionEntry[];
    audioTags: IGooglePixelAudioTagEntry[];
    waveform: IGooglePixelWaveformEntry[];
}

export interface IGooglePixelTranscriptionEntry {
    trackId?: string;
    sampleNumber: number;
    fileOffset?: string;
    rawText?: string;
    formattedText?: string;
    startTimeMs?: string;
    endTimeMs?: string;
    confidenceLevel?: string;
    confidenceScore?: string;
    overallConfidence?: string;
    locale?: string;
}

export interface IGooglePixelAudioTagEntry {
    trackId?: string;
    sampleNumber: number;
    fileOffset?: string;
    tagType?: string;
    timestampMs?: string;
    confidence?: string;
}

export interface IGooglePixelWaveformEntry {
    trackId?: string;
    sampleNumber: number;
    fileOffset?: string;
    timestampMs?: string;
    amplitude?: string;
}

export interface ILocationEntry {
    timestamp?: string;
    framing?: string;
    sequence?: string;
    latitude?: string;
    longitude?: string;
    altitude?: string;
    speed?: string;
    hdop?: string;
}

export interface ISiteEntry {
    timestamp?: string;
    frameCounter?: string;
    sessionHash?: string;
    recordIndex?: string;
    deviceSerial?: string;
    statusFlag?: string;
}

export interface ISensorEntry {
    sensorType?: string;
    counter?: string;
    fileOffset?: string;
    sampleBlock?: string;
}

export interface IDetectedFaceEntry {
    fileOffset?: string;
    faceId?: string;
    boundsTL?: string;
    boundsTR?: string;
    boundsBR?: string;
    boundsBL?: string;

    rollAngle?: string;
    yawAngle?: string;
}

export interface ILivePhotoInfoEntry {
    fileOffset?: string;
    sampleSize?: string;
    version?: string;
    exposureTime?: string;
    avgFocusScore?: string;
    ispMotion?: string;
    ispScale?: string;
    lensPosition?: string;
    afStatus?: string;
    focusingMethod?: string;
    fieldValidityMask?: string;
    faceCount?: string;
    faceTimestamp?: string;
    faceBounds?: string;
    faceId?: string;
    faceRollYaw?: string;
    faceFlags?: string;
    streamFaceCounts?: string;
    secFaceTimestamp?: string;
    secFaceBounds?: string;
    secFaceId?: string;
    secFaceRollYaw?: string;
    secFaceFlags?: string;
    v3ValidityFlags?: string;
    stabMatrix?: string;
    cmTimeHost1?: string;
    cmTimeHost2?: string;
    irisRecipe?: string;
    irisDisplacement?: string;
    interpolatedFrame?: string;
}